import React, { useState } from 'react';
import { Container, Row, Col, Button, Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faGamepad, faFlag, faLaptopCode } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import CTFStatsSection from './modules/home/ctfstats';
import WriteUpsSection from './modules/home/WriteUpsSection';
import ContactSection from './modules/home/contact-section';
import './LandingPage.css';

import Footer from './modules/global/footer';
import { useAuth } from './AuthContext';

function LandingPage() {
    const [showModal, setShowModal] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const [rememberMe, setRememberMe] = useState(false);
    const { authenticated, loading, login } = useAuth();

    const handleLogin = async () => {
        try {
            setErrorMessage('');
            const data = await login(username, password, rememberMe);

            if (data.status === 'success') { navigate('/dashboard'); return; }
            if (data.status === 'rate_limited') {
                const retry = Number(data.retry_after_seconds) || 300;
                const mins = Math.floor(retry / 60);
                const secs = retry % 60;
                setErrorMessage(`Too many failed logins. Try again in ${mins}m ${secs}s.`);
                return;
            }
            if (data.status === 'invalid_credentials') { setErrorMessage('Invalid username or password.'); return; }
            if (data.status === 'error') { setErrorMessage(data.message || 'Server error. Please try again.'); return; }
            setErrorMessage('Login failed. Please try again.');
        } catch {
            setErrorMessage('Network error. Please try again.');
        }
    };

    const handleOpenModal = () => {
        if (!loading && authenticated) {
            navigate('/dashboard');
        } else {
            setShowModal(true);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setErrorMessage('');
        setRememberMe(false);
    };

    return (
        <div className="landing-page">
            {/* Hero Section */}
            <section className="hero-section">
                <video autoPlay loop muted id="video-background">
                    <source src="/black.mp4" type="video/mp4" />
                    Your browser does not support video, please update your browser.
                </video>
                <div className="overlay">
                    <picture>
                        <source
                            media="(max-width: 768px)"
                            srcSet="/logo332.png"
                        />
                        <img
                            src="/logo.png"
                            alt="Fedoraman Project Logo"
                            className="hero-logo"
                            style={{
                                position: 'absolute',
                                top: '20px',
                                left: '20px',
                                maxWidth: '200px',
                                width: 'auto',
                                zIndex: 2
                            }}
                        />
                    </picture>
                    <Container fluid className="text-center text-white" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Row>
                            <Col>
                                <h1 className="high-tech-font">Fedoraman Project</h1>
                                <h2 className="red-team-title">
                                    <FontAwesomeIcon icon={faShieldAlt} /> Cybersecurity Red Team
                                </h2>
                                <p className="subtext">A group of ethical pentesters.</p>
                                <Button className="login-button" onClick={handleOpenModal}>
                                    Login
                                </Button>
                                <div className="scroll-indicator-container">
                                    <div className="digital-scroll">
                                        <div className="scroll-line"></div>
                                        <div className="scroll-text">SCROLL_</div>
                                        <div className="scroll-glitch"></div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </section>

            {/* Who We Are Section */}
            <section className="landing-page-about">
                <Container fluid className="text-white py-5">
                    <Row className="justify-content-center">
                        <Col md={10} lg={8} className="text-center mb-5">
                            <h2 className="landing-page-about-title high-tech-font">Who We Are</h2>
                            <div className="landing-page-about-underline"></div>
                            <p className="landing-page-about-subtitle">Just a small group of enthusiasts who love breaking things (ethically!)</p>
                        </Col>
                    </Row>

                    <Row className="justify-content-center g-4">
                        <Col md={6} lg={4}>
                            <div className="landing-page-about-card">
                                <div className="landing-page-about-icon-wrapper">
                                    <FontAwesomeIcon icon={faGamepad} className="landing-page-about-icon" />
                                </div>
                                <h3 className="landing-page-about-card-title">CTF Enthusiasts</h3>
                                <p className="landing-page-about-description">
                                    We are a bunch of folks that casually hop on to solve a few CTFs and to learn more about security.
                                </p>
                            </div>
                        </Col>

                        <Col md={6} lg={4}>
                            <div className="landing-page-about-card">
                                <div className="landing-page-about-icon-wrapper">
                                    <FontAwesomeIcon icon={faFlag} className="landing-page-about-icon" />
                                </div>
                                <h3 className="landing-page-about-card-title">What We Do</h3>
                                <p className="landing-page-about-description">
                                    From web exploitation to reverse engineering, we tackle CTF challenges and share our knowledge while having fun.
                                </p>
                            </div>
                        </Col>

                        <Col md={6} lg={4}>
                            <div className="landing-page-about-card">
                                <div className="landing-page-about-icon-wrapper">
                                    <FontAwesomeIcon icon={faLaptopCode} className="landing-page-about-icon" />
                                </div>
                                <h3 className="landing-page-about-card-title">Our Projects</h3>
                                <p className="landing-page-about-description">
                                    We create our own CTF challenges, tinker with security tools, and collaborate on various hacking projects.
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            <CTFStatsSection />

            <WriteUpsSection />

            <ContactSection />

            <Footer />

            {/* Login Modal */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                centered
                container={document.querySelector('.landing-page')}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Login</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errorMessage && (
                        <Alert variant="danger" style={{ marginBottom: '15px' }}>
                            {errorMessage}
                        </Alert>
                    )}
                    <Form>
                        <Form.Group controlId="formUsername">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group controlId="formPassword" style={{ marginTop: '10px' }}>
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group controlId="formRememberMe" style={{ marginTop: '15px' }}>
                            <Form.Check
                                type="checkbox"
                                label="Remember me for 6 months"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleLogin}>
                        Login
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default LandingPage;
